import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import styles from './PlasticJourney.module.css'

interface JourneyStep {
    index: string
    image: string
    imageAlt: string
    accent: string
    title: string
    description: string
    tag: string
}

const steps: JourneyStep[] = [
    {
        index: '01',
        image: '/brandbook/27-tortuga_playa.svg',
        imageAlt: 'Materiales plásticos recolectados en la costa de Veracruz junto a redes de pesca y lentes terminados',
        accent: 'var(--color-cover-blue)',
        title: 'La Costa',
        description:
            'Todo comienza en las playas de Veracruz, donde el plástico que el océano devuelve es recolectado por comunidades locales en apoyo con la fundación Yepez A.C. protegiendo el hábitad de diferentes especias de tortugas marinas. Redes, botellas y fragmentos, el mar descarta lo que nosotros transformamos.',
        tag: 'Veracruz, México',
    },
    {
        index: '02',
        image: '/brandbook/28-recolectando.svg',
        imageAlt: 'Bolsa de red "recicla-tortu-k" con plástico recolectado listo para procesar',
        accent: 'var(--color-cover-green)',
        title: 'La Recolección',
        description:
            'Cada bolsa de red lleva la etiqueta "recicla-tortu-k". Las comunidades ganan por cada kilo recuperado. La limpieza de playa es el primer paso de fabricación.',
        tag: 'Economía circular',
    },
    {
        index: '03',
        image: '/brandbook/29-maquina.svg',
        imageAlt: 'Detalle extremo del mecanismo de la carátula mostrando la textura del material reciclado',
        accent: 'var(--color-cover-yellow)',
        title: 'La Transformación',
        description:
            'El plástico se tritura, granula y reformula hasta convertirse en material de grado óptico. El proceso le da una textura característica — no la escondemos. Es la huella del origen, vuelta diseño.',
        tag: 'Plástico grado óptico',
    },
    {
        index: '04',
        image: '/brandbook/26_modelo_sol.svg',
        imageAlt: 'Usuario en la playa de Veracruz usando los lentes Tortu-k, con carátula azul',
        accent: 'var(--color-cover-red)',
        title: 'Playa a la vista',
        description:
            'El residuo que llegó a la playa termina en el rostro de quien sabe que el estilo puede tener historia. Cambia la carátula cuando quieras el marco dura para siempre.',
        tag: 'Diseñados por nosotros, armados por ti',
    },
]

function StepPanel({ step, i }: { step: JourneyStep; i: number }) {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { once: true, margin: '-15%' })

    return (
        <div ref={ref} className={styles.step}>
            <motion.div
                className={styles.stepImage}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
            >
                <img
                    src={step.image}
                    alt={step.imageAlt}
                    className={styles.stepImg}
                    loading="lazy"
                />
            </motion.div>

            <motion.div
                className={styles.stepText}
                initial={{ opacity: 0, x: i % 2 === 0 ? 40 : -40 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            >
                <p className={styles.stepIndex}>{step.index} /</p>
                <span className={styles.stepAccent} style={{ background: step.accent }} />
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepDescription}>{step.description}</p>
                <span className={styles.stepTag}>{step.tag}</span>
            </motion.div>
        </div>
    )
}

export function PlasticJourney() {
    return (
        <section className={styles.section} aria-label="El Viaje del Plástico">
            <div className={styles.header}>
                <p className={styles.eyebrow}>El proceso</p>
                <h2 className={styles.title}>El Viaje del Plástico</h2>
            </div>

            <div className={styles.steps} role="list">
                {steps.map((step, i) => (
                    <div key={step.index} role="listitem">
                        <StepPanel step={step} i={i} />
                    </div>
                ))}
            </div>
        </section>
    )
}
